import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ChevronRight } from 'lucide-react';
import { api } from '../../lib/api';
import InvoiceStatusChip from '../../components/crm/InvoiceStatusChip';

// Portal Invoices list — Sprint D item 3.2.
// Lists the principal's account invoices with the signature-required badge
// surfaced on each row. Full PaginatedTable retrofit deferred until the
// admin invoice list bears the same shape; for now a simple rows table.

function formatMoney(cents, currency = 'USD') {
  if (cents == null) return '—';
  const n = Number(cents) / 100;
  return n.toLocaleString('en-US', { style: 'currency', currency });
}

function formatDate(s) {
  if (!s) return '—';
  return s.slice(0, 10);
}

export default function PortalInvoices() {
  const [data, setData] = useState({ items: [], total: 0, signature_required_unsigned_count: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get('/api/client/invoices')
      .then((d) => { if (alive) setData(d); })
      .catch((err) => { if (alive) setError(err?.data?.error || err?.message || 'Failed to load invoices'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="portal-invoices" className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Invoices</span></h1>
        <p className="text-[13px] text-gray-400 mt-1">Your active invoices, payment history, and signature-required documents.</p>
      </div>

      {loading && <div className="text-[13px] text-gray-500">Loading…</div>}
      {error && <div className="text-[13px] text-red-300/90">{error}</div>}

      {!loading && !error && data.items.length === 0 && (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] px-6 py-10 text-center">
          <FileText className="w-8 h-8 text-gray-500 mx-auto mb-2" />
          <div className="text-[13px] text-gray-400">No invoices yet.</div>
        </div>
      )}

      {!loading && !error && data.items.length > 0 && (
        <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
          <table className="w-full text-[13px]" data-testid="portal-invoices-table">
            <thead className="bg-white/[0.02] text-gray-400">
              <tr className="text-left">
                <th className="px-4 py-2.5 font-medium">Invoice</th>
                <th className="hidden lg:table-cell px-4 py-2.5 font-medium">Issued</th>
                <th className="hidden lg:table-cell px-4 py-2.5 font-medium">Valid through</th>
                <th className="px-4 py-2.5 font-medium">Amount</th>
                <th className="px-4 py-2.5 font-medium">Status</th>
                <th className="px-4 py-2.5 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-white/[0.04]">
              {data.items.map((inv) => {
                const needsSignature = inv.signature_required === 1 && !inv.signed_at;
                return (
                  <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="hover:bg-white/[0.02]">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Link to={`/portal/invoices/${inv.id}`} className="text-white hover:text-[#B0FC38]">
                          {inv.invoice_number || inv.id.slice(0, 8)}
                        </Link>
                        {needsSignature && (
                          <span data-testid={`signature-dot-${inv.id}`} className="w-2 h-2 rounded-full bg-red-400 shadow shadow-red-500/40" title="Signature required" />
                        )}
                      </div>
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3 text-gray-400 whitespace-nowrap">{formatDate(inv.issued_at)}</td>
                    <td className="hidden lg:table-cell px-4 py-3 text-gray-400 whitespace-nowrap">{formatDate(inv.valid_through)}</td>
                    <td className="px-4 py-3 text-white">{formatMoney(inv.amount_cents)}</td>
                    <td className="px-4 py-3"><InvoiceStatusChip invoice={inv} audience="client" /></td>
                    <td className="px-4 py-3">
                      <Link to={`/portal/invoices/${inv.id}`} className="text-gray-400 hover:text-white">
                        <ChevronRight className="w-4 h-4" />
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
